import type { ComponentType } from 'react';
import { useParams } from 'react-router';
import type { SettingsSection as SettingsSectionKey } from './settingsNav';
import { OrganizationSection } from './sections/OrganizationSection';
import { MembersSection } from './sections/MembersSection';
import { BranchesSection } from './sections/BranchesSection';
import { BankSection } from './sections/BankSection';
import { BillingSection } from './sections/BillingSection';
import { BillingRulesSection } from './sections/BillingRulesSection';
import { DisplaySection } from './sections/DisplaySection';
import { AuditSection } from './sections/AuditSection';
import { ProfileSection } from './sections/ProfileSection';
import { PasswordSection } from './sections/PasswordSection';

/** One component per `/settings/:section` route, looked up by the dynamic
 *  segment. `SettingsLayout` guards unknown/forbidden sections before this
 *  ever mounts — it early-returns a `<Navigate/>` instead of rendering
 *  `<Outlet/>` — so the fallback here is defensive, not load-bearing.
 *  Non-component export from a component file, deliberately: the section
 *  test imports this map directly to assert its keys against the nav config. */
// eslint-disable-next-line react-refresh/only-export-components
export const SECTION_COMPONENTS: Record<SettingsSectionKey, ComponentType> = {
  organization: OrganizationSection,
  members: MembersSection,
  branches: BranchesSection,
  bank: BankSection,
  billing: BillingSection,
  'billing-rules': BillingRulesSection,
  display: DisplaySection,
  audit: AuditSection,
  profile: ProfileSection,
  password: PasswordSection,
};

export function SettingsSection() {
  const { section } = useParams<{ section: SettingsSectionKey }>();
  const Component = section && Object.hasOwn(SECTION_COMPONENTS, section) ? SECTION_COMPONENTS[section as keyof typeof SECTION_COMPONENTS] : undefined;
  return Component ? <Component /> : null;
}
